import { useEffect, useMemo, useState } from "react";
import classNames from "classnames";
import { twMerge } from "tailwind-merge";

import Tick from "@/common/Tick";
import { Colors } from "@/constants/colors";
import { ExperimentIds } from "@/constants/experiments";
import { questionGroups, questionGroupsTitles } from "@/constants/questionGroups";
import { Question } from "@/types/question";
import { useExperiment } from "@/utils/ExperimentsProvider";
import { useTheme } from "@/utils/ThemeProvider";

import { ReactComponent as BackSVG } from "../assets/img/header/btn.svg";

type QuestionBlocks = Question[][];
interface HeaderProps {
    step?: number;
    prevTitle?: questionGroups;
    title: questionGroups;
    onBack?: () => void;
    targetQuestions?: Question[];
    allPossibleQuestions?: Question[];
    isFinished?: boolean;
    className?: string;
    questionId: string;
}
const Header = ({
    step = 0,
    prevTitle: prevTitleKey,
    title: titleKey,
    onBack,
    targetQuestions: allQuestions = [],
    allPossibleQuestions,
    isFinished = false,
    questionId,
    className,
}: HeaderProps) => {
    const { isLightTheme } = useTheme();
    const title = questionGroupsTitles[titleKey];
    const prevTitle = prevTitleKey ? questionGroupsTitles[prevTitleKey] : "";

    const targetQuestions = useMemo(
        () => allQuestions.filter((question) => !question.withoutHeader),
        [allQuestions]
    );

    const { isTest: isNumberOfStepsExp } = useExperiment({
        experimentId: ExperimentIds.NUMBER_OF_STEPS,
    });

    const blockQuestions = useMemo(() => {
        const firstBreakpoint = targetQuestions.find((q) => q.progressBarBreakPoint);
        const secondBreakpoint = targetQuestions.find(
            (q) => q.progressBarBreakPoint && q !== firstBreakpoint
        );

        const firstPart = firstBreakpoint
            ? targetQuestions.slice(0, targetQuestions.indexOf(firstBreakpoint) + 1)
            : [];
        const secondPart =
            firstBreakpoint && secondBreakpoint
                ? targetQuestions.slice(
                      targetQuestions.indexOf(firstBreakpoint) + 1,
                      targetQuestions.indexOf(secondBreakpoint) + 1
                  )
                : [];
        const thirdPart = secondBreakpoint
            ? targetQuestions.slice(targetQuestions.indexOf(secondBreakpoint) + 1)
            : [];

        return [firstPart, secondPart, thirdPart];
    }, [targetQuestions]);

    const blockQuestionsWithTitle = useMemo(
        () =>
            allPossibleQuestions?.reduce<{ [key: string]: Question[] }>((acc, q) => {
                const { groupTitle } = q;

                return groupTitle
                    ? {
                          ...acc,
                          [groupTitle]: acc[groupTitle] ? [...acc[groupTitle], q] : [q],
                      }
                    : acc;
            }, {}) ?? {},
        [allPossibleQuestions]
    );

    const [questionsTotal, setQuestionsTotal] = useState<number | null>(null);
    const [questionsStep, setQuestionsStep] = useState<number | null>(null);
    useEffect(() => {
        let step = 0;
        const total =
            Object.values(blockQuestionsWithTitle).find((questions) =>
                questions.some(({ id }, i) => {
                    if (questionId === id) {
                        step = ++i;
                        return true;
                    }
                    return false;
                })
            )?.length ?? 0;

        step && setQuestionsStep(step);
        total && setQuestionsTotal(total);
    }, [questionId, blockQuestionsWithTitle]);

    const countFilledWidth = (blockQuestions: QuestionBlocks, step: number) => {
        const stepCount = blockQuestions.length;
        const firstBlockLength = blockQuestions[0].length;
        const secondBlockLength = blockQuestions[1].length;
        const thirdBlockLength = blockQuestions[2].length;
        if (step <= firstBlockLength) {
            return (step * 100) / stepCount / firstBlockLength / 100 + (step === 0 ? 0 : 0.02); // need to cover breakpoint dot
        } else if (step > firstBlockLength && step <= firstBlockLength + secondBlockLength) {
            return (
                (firstBlockLength * 100) / stepCount / firstBlockLength / 100 +
                0.02 + // need to cover breakpoint dot
                ((step - firstBlockLength) * 100) / stepCount / secondBlockLength / 100
            );
        } else {
            return (
                (firstBlockLength * 100) / stepCount / firstBlockLength / 100 +
                (secondBlockLength * 100) / stepCount / secondBlockLength / 100 +
                ((step - firstBlockLength - secondBlockLength) * 100) /
                    stepCount /
                    thirdBlockLength /
                    100
            );
        }
    };

    const createProgressDelimiters = (blockQuestions: QuestionBlocks, step: number) => {
        const elements = [];
        for (let i = 0; i < blockQuestions.length + 1; i++) {
            const blocks = blockQuestions.reduce<QuestionBlocks>(
                (acc, block) => [...acc, [...block, ...acc.flat()]],
                []
            );

            const isDone = isFinished || (i > 0 && step >= blocks[i - 1].length);
            elements.push(
                <Tick
                    key={i}
                    className={classNames(
                        "-top-0.5 box-content h-3 w-3 rounded-full border-2 border-black text-zing-9-14 font-semibold text-black female:border-white female:text-white",
                        {
                            "bg-zing-black-200 female:bg-zing-neutral-cool-grey-200": !isDone,
                            "bg-zing-warm-neon-green-500 female:bg-zing-warm-tangerine-500":
                                i === 0 || isDone,
                        }
                    )}
                    label={isDone ? undefined : ""}
                />
            );
        }
        return elements;
    };

    const hideBackButton = step < 1 || !onBack;

    return (
        <div
            className={twMerge(
                "flex flex-col items-center gap-1.5 px-6 pt-4 text-center text-white female:text-black md:py-10",
                className
            )}
        >
            <div className="flex w-full flex-row">
                <div className="flex w-full flex-col items-center">
                    <div className="flex min-h-[18px] min-w-fit gap-1 text-zing-12-18 font-medium uppercase text-white female:text-black">
                        <span
                            className={classNames(
                                "flex items-center gap-0.5 transition-transform duration-700",
                                {
                                    "translate-y-[-100px]": !title,
                                    "translate-y-[0px]": title,
                                }
                            )}
                        >
                            {title ?? prevTitle}
                            {isNumberOfStepsExp && questionsTotal && questionsStep && (
                                <span className="pl-1 font-semibold text-zing-warm-neon-green-500 female:text-zing-warm-tangerine-500">
                                    {questionsStep} / {questionsTotal}
                                </span>
                            )}
                        </span>
                    </div>
                </div>
            </div>
            <div className="relative flex h-7 w-full flex-row items-center gap-2">
                <div
                    className={classNames("absolute flex items-center duration-500", {
                        "-left-14 opacity-0": hideBackButton,
                        "opacity-1 left-0": !hideBackButton,
                    })}
                >
                    <button
                        className="flex h-7 w-7 items-center justify-center rounded-lg bg-zing-black-200 p-2 active:text-zing-blue-200 female:bg-zing-neutral-cool-grey-200"
                        onClick={onBack}
                    >
                        <BackSVG
                            className="md:h-4"
                            color={isLightTheme ? Colors.BLACK : Colors.WHITE}
                        />
                    </button>
                </div>
                <div
                    className={classNames(
                        "relative h-1 w-full rounded bg-zing-black-200 transition-[margin] duration-500 female:bg-zing-neutral-cool-grey-200",
                        {
                            "ml-9": !hideBackButton,
                            "ml-0": hideBackButton,
                        }
                    )}
                >
                    <div className="absolute -top-1 z-10 flex w-full justify-between">
                        {createProgressDelimiters(blockQuestions, step)}
                    </div>
                    <div
                        className="absolute h-full w-0 max-w-full rounded bg-zing-warm-neon-green-500 transition-width duration-300 female:bg-zing-warm-tangerine-500"
                        style={{
                            width: `${
                                // made to cover second part of the questions
                                targetQuestions.length > 2
                                    ? countFilledWidth(blockQuestions, step) * 100
                                    : 100
                            }%`,
                        }}
                    />
                </div>
            </div>
        </div>
    );
};

export default Header;
